import React from 'react';
import { TechLayout } from '../../components/tech/TechLayout';
import { H3, Lead, Purpose, P, TCap, Fig, DataTable, Code, Video } from '../../components/tech/TechUI';
import { T } from '../../data/techSections';
import { FORMAT_1, FORMAT_2 } from './labelFormats';
import { MetadataSection } from './MetadataSection';

const TYPES = [
  ['Task', 'Industry, task and environment', 'Electronics · soldering · assembly bench', 'Picking footage by job'],
  ['Action segments', 'What the worker is doing over time, and whether it is part of the job', 'Scanning a parcel · on-task', 'Learning tasks step by step; cutting idle time'],
  ['Hand–object interactions', 'Which hand acts on which object, with which tool, and whether the object changed', 'Right hand cuts a weed with a sickle', 'Learning manipulation; mapping human actions to robot actions'],
  ['Hand keypoints', '21 joints per hand on every frame', 'See the hand pose figures', '3D hand pose; retargeting the motion onto a robot hand'],
  ['Objects and scene', 'Objects, tools, environment and lighting', 'Circuit board, tweezers · factory · bright', 'Finding the right footage'],
  ['Camera visibility', 'For each action, which cameras see the hand and the object', 'Left eye: yes · left front: partly', 'Choosing the best view'],
];

const TILES = [['95.6%', 'Action observability'], ['89.8%', 'Hand–object visibility'], ['4.2 / 6', 'Camera views per action'], ['99.0%', 'Execution visible'], ['98.3%', 'Complete interactions (footage)']];

const METRICS = [
  ['Action observability', '95.6%', 'Actions can be recognised'],
  ['Execution visibility', '99.0%', 'The manipulation itself is on camera'],
  ['Hand–object visibility', '89.8%', 'Hand and object both in view'],
  ['Multi-view coverage', '4.2 of 6 views', 'Each action seen from several angles'],
  ['Multi-view redundancy', '99.5%', 'Seen by at least two cameras'],
  ['Action completeness', '98.3%', 'Reach to outcome, all on camera'],
  ['Generated label density', '≈ 924 / h', 'Candidate actions labelled per hour'],
  ['Physical interaction density', '≈ 914 / h', 'Real hand–object interactions per hour'],
];

const ACC_TILES = [['≈ 90%', 'Action label accuracy'], ['92.6%', 'Category accuracy'], ['95%', 'Labelled hand on camera'], ['88.8%', 'Tool correct']];

const ACCURACY = [
  ['Action label accuracy', '≈ 90%', 'Action, object and hand labelled correctly'],
  ['Category accuracy', '92.6%', 'Each recording filed under the right category'],
  ['Hand check', '95%', 'The hand named in the label is visible doing it'],
  ['Tool accuracy', '88.8%', 'The right tool named'],
];

const Tiles = ({ items, cols, testid }) => (
  <div className={`grid grid-cols-2 ${cols} gap-2 mb-4`} data-testid={testid}>
    {items.map(([v, l]) => (
      <div key={l} className="rounded-xl border border-white/10 bg-[#141414] p-4">
        <div><b className="font-mono text-2xl font-bold gradient-text">{v}</b></div>
        <span className="text-xs text-white/45">{l}</span>
      </div>
    ))}
  </div>
);

export default function LabelsPage() {
  return (
    <TechLayout slug="labels" lead={<Lead>Every recording is labelled from the task down to each hand action, using all six cameras, and ships with a full metadata record.</Lead>}>
      <H3 id="label-types">Label types</H3>
      <TCap title="Label types." />
      <DataTable head={['Label', 'What it tells you', 'Example', 'Used for']} rows={TYPES} testid="table-label-types" min={760} />

      <H3 id="labelled-recordings">Labelled recordings</H3>
      <Purpose>Three levels: an instruction, steps, and timed hand actions.</Purpose>
      <Fig cap="Two labelled recordings, six cameras in sync.">
        <div className="grid md:grid-cols-2 gap-4">
          <Video name="videos/labels-pcb" caption="Electronics · inspecting, trimming and placing a circuit board" />
          <Video name="videos/labels-packing" caption="Electronics · labelling and packing components" />
        </div>
      </Fig>

      <TCap title="How labellable the footage is.">10 hours, model-audited.</TCap>
      <Tiles items={TILES} cols="md:grid-cols-5" testid="label-tiles" />
      <DataTable head={['Metric', 'Result', 'What it means']} right={[1]} rows={METRICS} testid="table-label-metrics" />

      <Fig cap="One real manipulation, four synced cameras. Below: its stages from the right-front camera; the timings are in Format 2 below.">
        <Video name="videos/sequence-q93htt" caption="Electronics · soldering: swab out, along the pads, back in the pot; iron tip cleaned" />
        <img src={T('videos/sequence-q93htt-stages.jpg')} alt="Six frames from the right-front camera showing the soldering stages" className="w-full block mt-3 rounded-xl border border-white/10" loading="lazy" />
      </Fig>

      <H3 id="label-accuracy">Label accuracy</H3>
      <Tiles items={ACC_TILES} cols="md:grid-cols-4" testid="label-accuracy-tiles" />
      <DataTable head={['Metric', 'Result', 'What it means']} right={[1]} rows={ACCURACY} testid="table-label-accuracy" min={560} />

      <H3 id="label-structure">Label structure</H3>
      <TCap title="Format 1 · Steps and hand actions">(the circuit board video above)</TCap>
      <Code testid="code-format-1">{FORMAT_1}</Code>
      <TCap title="Format 2 · Manipulation stages">(the soldering video above)</TCap>
      <Code testid="code-format-2">{FORMAT_2}</Code>

      <H3 id="action-labels-with-hand-tracking">Action labels with hand tracking</H3>
      <Purpose>Labelled at full resolution; hand tracking confirms which hand acted.</Purpose>
      <Fig cap="Blue: left hand. Orange: right hand.">
        <Video name="videos/labels-fullres-hands" caption="Logistics · tagging garments: action labels with hand tracking" />
      </Fig>
      <P><b className="text-white/90">Example, in the video above.</b> At 0:17 the label reads "left hand: pick up circular tag". The tracked left hand moves 5× more than the right, so it really is the left hand. At 0:35 the model said "both hands", but tracking shows only the right hand placing the garment, so the label was corrected to "right hand".</P>
      <MetadataSection />
    </TechLayout>
  );
}
